import { useCallback, useEffect, useRef, useState } from 'react'
import { PrintAgentBadge } from '@/components/features/tasks/PrintAgentBadge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { useLocalPrintAgent, isLocalPrintAgentOnline } from '@/hooks/useLocalPrintAgent'
import { useToast } from '@/hooks/useToast'
import { extractApiError } from '@/lib/api'
import { printApi, resolveAgentId } from '@/lib/api/print'
import { shipmentsApi } from '@/lib/api/shipments'
import { useWorkerStore } from '@/stores/worker.store'

interface WorkerPrintPalletModalProps {
  onClose: () => void
}

type StatusMessage = { type: 'info' | 'error' | 'success'; message: string }

export function WorkerPrintPalletModal({ onClose }: WorkerPrintPalletModalProps) {
  const { toast } = useToast()
  const workerName = useWorkerStore(s => s.workerName)
  const { data: localAgent } = useLocalPrintAgent()
  const agentOnline = isLocalPrintAgentOnline(localAgent)
  const [code, setCode] = useState('')
  const [copies, setCopies] = useState(1)
  const [status, setStatus] = useState<StatusMessage | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [comCapture, setComCapture] = useState(false)
  const lastScanTsRef = useRef(0)
  const prevScanModeRef = useRef<'box_scan' | 'capture'>('box_scan')
  const comCaptureRef = useRef(false)
  const submittingRef = useRef(false)
  const codeInputRef = useRef<HTMLInputElement>(null)

  const focusCodeInput = useCallback(() => {
    codeInputRef.current?.focus()
  }, [])

  const submit = useCallback(
    async (rawCode: string) => {
      const trimmed = rawCode.trim()
      if (!trimmed || submittingRef.current) return

      submittingRef.current = true
      setIsSubmitting(true)
      setStatus(null)

      try {
        const { data } = await shipmentsApi.searchCode(trimmed)
        if (!data.found) {
          setStatus({ type: 'error', message: data.message || 'Код не найден' })
          return
        }
        if (data.type !== 'box' && data.type !== 'pallet') {
          setStatus({ type: 'error', message: 'Нужен КА коробки или паллеты' })
          return
        }
        if (!data.task_id) {
          setStatus({ type: 'error', message: 'Не удалось определить задание' })
          return
        }
        if (data.pallet_num == null) {
          setStatus({ type: 'error', message: 'Не удалось определить паллету' })
          return
        }

        const agentId = await resolveAgentId({ localOnly: true })
        const copyCount = copies >= 1 ? copies : 1
        await printApi.printPallet(data.task_id, data.pallet_num, {
          agent_id: agentId,
          worker_name: workerName.trim() || 'worker',
          copies: copyCount,
          force: true, // modal: reprint OK regardless of printed status
        })
        const taskHint = data.task_display_id ? ` · задание ${data.task_display_id}` : ''
        toast.success(`Печать КА паллеты №${data.pallet_num}${taskHint}`)
        setStatus({
          type: 'success',
          message: `Паллета №${data.pallet_num}${taskHint}, ${copyCount} эт.`,
        })
      } catch (err) {
        setStatus({ type: 'error', message: extractApiError(err) })
      } finally {
        submittingRef.current = false
        setIsSubmitting(false)
        setCode('')
        // Enter / toast / re-render may steal focus — keep scan field ready
        window.setTimeout(focusCodeInput, 50)
      }
    },
    [copies, focusCodeInput, toast, workerName],
  )

  useEffect(() => {
    focusCodeInput()
  }, [focusCodeInput, agentOnline])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const [agentStatus, cfg] = await Promise.all([
        printApi.getLocalAgentStatus(),
        printApi.getLocalAgentConfig(),
      ])
      if (cancelled) return

      const hasCom = Boolean(agentStatus?.online && agentStatus.scan_port?.trim())
      if (!hasCom) return

      prevScanModeRef.current =
        cfg?.scan_mode === 'capture' || cfg?.scan_mode === 'box_scan'
          ? cfg.scan_mode
          : 'box_scan'
      await printApi.putLocalAgentConfig({
        scan_mode: 'capture',
      })
      lastScanTsRef.current = Date.now()
      comCaptureRef.current = true
      setComCapture(true)
    })()

    return () => {
      cancelled = true
      if (comCaptureRef.current) {
        void printApi.putLocalAgentConfig({ scan_mode: prevScanModeRef.current })
        comCaptureRef.current = false
      }
    }
  }, [])

  useEffect(() => {
    if (!comCapture) return
    const id = window.setInterval(() => {
      void (async () => {
        const res = await printApi.getLastScan(lastScanTsRef.current)
        if (!res?.code || res.ts == null) return
        if (res.ts <= lastScanTsRef.current) return
        lastScanTsRef.current = res.ts
        setCode(res.code)
        await submit(res.code)
      })()
    }, 300)
    return () => clearInterval(id)
  }, [comCapture, submit])

  const statusClass =
    status?.type === 'error'
      ? 'text-red-600'
      : status?.type === 'success'
        ? 'text-green-600'
        : 'text-gray-600'

  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Header onClose={onClose}>Печать КА паллеты</Modal.Header>
      <Modal.Body>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <p className="text-sm text-gray-500">
            Отсканируйте КА коробки или паллеты — поиск по всем заданиям
          </p>
          <PrintAgentBadge />
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              КА коробки / паллеты
            </label>
            <Input
              ref={codeInputRef}
              autoFocus
              value={code}
              placeholder="Отсканируйте или введите код"
              // Keep enabled while submit — disabled blurs field; submittingRef blocks double Enter
              disabled={!agentOnline}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void submit(code)
                }
              }}
            />
            {comCapture && (
              <p className="text-xs text-gray-500 mt-1">COM-сканер активен — сканируйте код</p>
            )}
            {!agentOnline && (
              <p className="text-xs text-amber-700 mt-1">
                Запустите print-agent на этом ПК, чтобы печатать
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Количество этикеток
            </label>
            <Input
              type="number"
              min={1}
              value={copies}
              disabled={isSubmitting || !agentOnline}
              onChange={e => setCopies(Math.max(1, parseInt(e.target.value, 10) || 1))}
            />
          </div>
          {status && <p className={`text-sm ${statusClass}`}>{status.message}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
          Отмена
        </Button>
        <Button
          onClick={() => void submit(code)}
          isLoading={isSubmitting}
          disabled={!code.trim() || !agentOnline}
        >
          Печать
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
